import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logsAPI } from '../../services/api';
import './LogsHub.css';

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true, second: '2-digit' });
}

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576)    return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024)       return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
}

// Merges the session's embedded accountingUpdates[], the linked
// AuthorizationSession's timeline[] (when present), and syslog entries
// into one chronological event list — the "session investigation
// timeline" this page exists to show.
function buildTimeline({ session, syslogEntries, authSession }) {
    const events = [];

    if (session?.startTime) {
        events.push({ event: 'SESSION_START', status: 'success', timestamp: session.startTime, message: `Session started (${session.authMethod || 'unknown method'})` });
    }
    (session?.accountingUpdates || []).forEach(u => {
        events.push({ event: 'ACCOUNTING_INTERIM', status: 'info', timestamp: u.timestamp, message: `${formatBytes(u.upload)} up / ${formatBytes(u.download)} down` });
    });
    if (session?.stopTime) {
        events.push({ event: 'SESSION_STOP', status: session.status === 'error' ? 'error' : 'warning', timestamp: session.stopTime, message: session.terminationCause || 'stopped' });
    }
    (authSession?.timeline || []).forEach(e => {
        events.push({ event: e.event, status: (e.status || 'info').toLowerCase(), timestamp: e.timestamp, message: e.message, durationMs: e.durationMs });
    });
    (syslogEntries || []).slice(0, 100).forEach(s => {
        events.push({ event: `SYSLOG_${(s.category || 'EVENT').toUpperCase()}`, status: s.isThreat ? 'error' : 'info', timestamp: s.receivedAt, message: s.message });
    });

    return events
        .filter(e => e.timestamp)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

export default function SessionInvestigation() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState('timeline');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        logsAPI.getSessionDetail(sessionId)
            .then(r => setData(r.data.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [sessionId]);

    if (loading) return <div className="lh-wrap"><p className="lh-loading">Loading…</p></div>;
    if (!data)   return <div className="lh-wrap"><p className="lh-empty">Session not found.</p></div>;

    const { session, syslogEntries, authSession, device } = data;
    const timeline = buildTimeline({ session, syslogEntries, authSession });

    return (
        <div className="lh-wrap">
            <div className="lh-header">
                <button className="lh-back" onClick={() => navigate('/logs')}>← Back</button>
                <h1 className="lh-title">{session.sessionId}</h1>
                <span className={`lh-badge lh-badge-${session.status}`}>{session.status}</span>
            </div>

            <div className="lh-summary">
                <Info label="User"          value={session.userId?.name || session.userId?.radiusUsername} />
                <Info label="MAC"           value={session.deviceMac} mono />
                <Info label="Framed IP"     value={session.framedIpAddress} mono />
                <Info label="NAS"           value={session.nasIdentifier || session.nasIpAddress} />
                <Info label="Auth Method"   value={session.authMethod} />
                <Info label="Plan"          value={session.planId?.name} />
                <Info label="Correlation ID" value={session.correlationId} mono />
                <Info label="Started"       value={fmt(session.startTime)} />
                <Info label="Stopped"       value={fmt(session.stopTime)} />
                <Info label="Duration"      value={session.sessionDuration ? `${Math.floor(session.sessionDuration / 60)}m` : '—'} />
                <Info label="Data Usage"    value={formatBytes(session.dataUsage?.total)} />
                <Info label="Termination"   value={session.terminationCause} />
            </div>

            <div className="lh-tabs">
                {['timeline', 'session', 'device'].map(t => (
                    <button key={t} className={`lh-tab${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                ))}
            </div>

            <div className="lh-body">
                {tab === 'timeline' && <TimelineTab events={timeline} />}
                {tab === 'session'  && <SessionTab session={session} authSession={authSession} />}
                {tab === 'device'   && <DeviceTab device={device} />}
            </div>
        </div>
    );
}

function TimelineTab({ events }) {
    if (!events.length) return <p className="lh-empty">No timeline events recorded.</p>;
    return (
        <div className="lh-timeline">
            {events.map((e, i) => (
                <div key={i} className={`lh-event lh-event-${e.status}`}>
                    <div className="lh-event-icon">●</div>
                    <div className="lh-event-body">
                        <div className="lh-event-name">{e.event}</div>
                        {e.message && <div className="lh-event-msg">{e.message}</div>}
                        <div className="lh-event-time">{fmt(e.timestamp)}{e.durationMs ? ` · ${e.durationMs}ms` : ''}</div>
                    </div>
                    <span className={`lh-badge lh-badge-${e.status}`}>{e.status}</span>
                </div>
            ))}
        </div>
    );
}

function SessionTab({ session, authSession }) {
    return (
        <div className="lh-section">
            <Row label="Session ID"      value={session.sessionId} mono />
            <Row label="Tenant ID"       value={session.tenantId}  mono />
            <Row label="Branch ID"       value={session.branchId}  mono />
            <Row label="NAS IP"          value={session.nasIpAddress} mono />
            <Row label="NAS Port"        value={session.nasPort} />
            <Row label="AP MAC"          value={session.location?.apMac} mono />
            <Row label="Upload"          value={formatBytes(session.dataUsage?.upload)} />
            <Row label="Download"        value={formatBytes(session.dataUsage?.download)} />
            {authSession && <Row label="Linked Auth Session" value={authSession.authSessionId} mono />}
            {!authSession && <Row label="Linked Auth Session" value="No portal/PMS auth attempt linked" />}
        </div>
    );
}

function DeviceTab({ device }) {
    if (!device) return <p className="lh-empty">No device record found for this MAC.</p>;
    return (
        <div className="lh-section">
            <Row label="Name"           value={device.name} />
            <Row label="Type"           value={device.type} />
            <Row label="Status"         value={device.status} />
            <Row label="Session Status" value={device.sessionStatus} />
            <Row label="Current IP"     value={device.currentIp} mono />
            <Row label="Last Seen"      value={fmt(device.lastSeenAt)} />
        </div>
    );
}

function Info({ label, value, mono }) {
    return (
        <div className="lh-info">
            <span className="lh-info-label">{label}</span>
            <span className={`lh-info-value${mono ? ' lh-mono' : ''}`}>{value || '—'}</span>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="lh-row">
            <span className="lh-row-label">{label}</span>
            <span className={`lh-row-value${mono ? ' lh-mono' : ''}`}>{value || '—'}</span>
        </div>
    );
}
